import React, { Fragment, useEffect, useState } from 'react';
import { Modal, Form, Button, Spinner, Table } from 'react-bootstrap';
import { MdQuestionAnswer, MdDelete, MdAdd } from 'react-icons/md';
import { toast } from 'react-toastify';
import { jobPostApi } from '@/_services/api/jobPost';
import { BootstrapToolTip } from '@/_elements/ToolTip';
import { FullViewLoadingSpinner } from '@/_elements/Loaders';
import ID from '@/_helpers/uuid';

export default function JobPostQuestionsForm({ item }) {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [questions, setQuestions] = useState([]);

    const handleShow = () => setShow(true);
    const handleClose = () => setShow(false);

    const fetchQuestions = async () => {
        try {
            setLoading(true);
            const data = await jobPostApi.getJobPostQuestions(item.id || item._id);
            setQuestions(data || []);
        } catch (error) {
            toast.error(error?.message || 'Failed to fetch questions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (show) {
            fetchQuestions();
        }
    }, [show]);

    const handleAddQuestion = () => {
        setQuestions([
            ...questions,
            {
                id: ID.uuid(),
                fieldName: '',
                type: 'textbox',
                required: false,
                data: [],
            },
        ]);
    };

    const handleDeleteQuestion = (index) => {
        const newQuestions = [...questions];
        newQuestions.splice(index, 1);
        setQuestions(newQuestions);
    };

    const handleQuestionChange = (index, field, value) => {
        const newQuestions = [...questions];
        newQuestions[index][field] = value;
        setQuestions(newQuestions);
    };

    const handleOptionChange = (qIndex, oIndex, value) => {
        const newQuestions = [...questions];
        if (!newQuestions[qIndex].data) newQuestions[qIndex].data = [];
        newQuestions[qIndex].data[oIndex] = value;
        setQuestions(newQuestions);
    };

    const handleAddOption = (qIndex) => {
        const newQuestions = [...questions];
        if (!newQuestions[qIndex].data) newQuestions[qIndex].data = [];
        newQuestions[qIndex].data.push('');
        setQuestions(newQuestions);
    };

    const handleDeleteOption = (qIndex, oIndex) => {
        const newQuestions = [...questions];
        if (!newQuestions[qIndex].data) newQuestions[qIndex].data = [];
        newQuestions[qIndex].data.splice(oIndex, 1);
        setQuestions(newQuestions);
    };

    const handleSave = async () => {
        try {
            const processedQuestions = questions.map((q, index) => {
                const trimmedFieldName = q.fieldName.trim();
                let processedData = [];

                if (q.type === 'select' || q.type === 'checkbox') {
                    // Trim and filter empty options
                    const cleaned = (q.data || []).map((opt) => opt.trim()).filter((opt) => opt !== '');
                    // Remove duplicates
                    processedData = [...new Set(cleaned)];

                    if (processedData.length === 0) {
                        throw new Error(`Question ${index + 1} must have at least one valid option`);
                    }

                    if (processedData.length < cleaned.length) {
                        throw new Error(`Question ${index + 1} has duplicate options`);
                    }
                }

                if (!trimmedFieldName) {
                    throw new Error(`Question ${index + 1} title cannot be empty`);
                }

                return {
                    ...q,
                    fieldName: trimmedFieldName,
                    data: processedData,
                };
            });

            setSubmitting(true);
            await jobPostApi.updateJobPostQuestions(item.id || item._id, processedQuestions);
            toast.success('Questions saved successfully');
            handleClose();
        } catch (error) {
            toast.error(error?.message || 'Failed to save questions');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Fragment>
            <BootstrapToolTip text="Job Post Questions">
                <MdQuestionAnswer className="icon view" onClick={handleShow} />
            </BootstrapToolTip>

            <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Manage Questions for: {item.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loading ? (
                        <FullViewLoadingSpinner />
                    ) : (
                        <Fragment>
                            <Table responsive bordered hover>
                                <thead>
                                    <tr>
                                        <th>SN</th>
                                        <th style={{ minWidth: '30%' }}>Question/Field Name</th>
                                        <th>Type</th>
                                        <th>Required</th>
                                        <th style={{ width: '30%' }}>Options</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {questions.map((q, index) => (
                                        <tr key={q.id || index}>
                                            <td>{index + 1}</td>
                                            <td>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={2}
                                                    value={q.fieldName}
                                                    onChange={(e) =>
                                                        handleQuestionChange(index, 'fieldName', e.target.value)
                                                    }
                                                    placeholder="Enter Question"
                                                />
                                            </td>
                                            <td>
                                                <Form.Control
                                                    as="select"
                                                    value={q.type}
                                                    onChange={(e) =>
                                                        handleQuestionChange(index, 'type', e.target.value)
                                                    }
                                                >
                                                    <option value="textbox">Textbox</option>
                                                    <option value="select">Select</option>
                                                    <option value="checkbox">Checkboxes</option>
                                                </Form.Control>
                                            </td>
                                            <td className="text-center">
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={q.required}
                                                    onChange={(e) =>
                                                        handleQuestionChange(index, 'required', e.target.checked)
                                                    }
                                                />
                                            </td>
                                            <td>
                                                {(q.type === 'select' || q.type === 'checkbox') && (
                                                    <div className="options-container">
                                                        {(q.data || []).map((opt, oIndex) => (
                                                            <div key={oIndex} className="d-flex mb-1">
                                                                <Form.Control
                                                                    size="sm"
                                                                    value={opt}
                                                                    onChange={(e) =>
                                                                        handleOptionChange(
                                                                            index,
                                                                            oIndex,
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    placeholder={`Option ${oIndex + 1}`}
                                                                />
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    className="ml-1"
                                                                    onClick={() => handleDeleteOption(index, oIndex)}
                                                                >
                                                                    <MdDelete size={14} />
                                                                </Button>
                                                            </div>
                                                        ))}
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            className="mt-1"
                                                            onClick={() => handleAddOption(index)}
                                                        >
                                                            <MdAdd size={14} /> Add Option
                                                        </Button>
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDeleteQuestion(index)}
                                                >
                                                    <MdDelete />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {questions.length === 0 && (
                                        <tr>
                                            <td colSpan="6" className="text-center text-muted">
                                                No questions added yet.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                            <Button variant="outline-success" className="mt-2" onClick={handleAddQuestion}>
                                <MdAdd /> Add New Question
                            </Button>
                        </Fragment>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={submitting || loading}>
                        {submitting ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="mr-1"
                                />
                                Saving...
                            </>
                        ) : (
                            'Save Questions'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
}
